import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Mail, 
  BookOpen, 
  ArrowLeft, 
  Copy, 
  CheckCircle,
  Key,
  User,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const PasswordRecovery = () => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Predefined tokens and user info for demo/testing
  const testUsers = [
    { username: 'OmmNew', email: 'ommnew@comica.test', resetToken: 'OMMNEW-RESET-2024' },
    { username: 'demo', email: 'demo@comica.test', resetToken: 'DEMO-RESET-2024' },
    { username: 'manual_test', email: 'manual@test.com', resetToken: 'MANUAL-RESET-2024' }
  ];

  const handleUserLookup = () => {
    const foundUser = testUsers.find(u => 
      u.username.toLowerCase() === username.toLowerCase() || 
      u.email.toLowerCase() === email.toLowerCase()
    );

    if (foundUser) {
      setUsername(foundUser.username);
      setEmail(foundUser.email);
      setResetToken(foundUser.resetToken);
      setStep(2);
      toast.success('User found! Here is your reset information.');
    } else {
      toast.error('User not found. Please check your username or email.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in-up">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center">
            <Key className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold gradient-text font-fredoka">Password Recovery</h2>
          <p className="text-gray-600 mt-2">Simple password reset helper</p>
        </div>

        {step === 1 && (
          <Card className="glass-card animate-slide-in-right">
            <CardHeader>
              <CardTitle className="text-center flex items-center justify-center space-x-2">
                <User className="w-5 h-5" />
                <span>Find Your Account</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertDescription>
                  Enter your username OR email to get password reset instructions.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username (e.g., OmmNew)"
                    className="form-input"
                  />
                </div>

                <div className="text-center text-sm text-gray-500">OR</div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input"
                  />
                </div>
              </div>

              <Button 
                onClick={handleUserLookup}
                disabled={!username.trim() && !email.trim()}
                className="w-full btn-primary"
                size="lg"
              >
                <User className="w-4 h-4 mr-2" />
                Find My Account
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm font-medium text-purple-600 hover:text-purple-500 transition-colors">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="glass-card animate-slide-in-right">
            <CardHeader>
              <CardTitle className="text-center flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Account Found!</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-3">Your Account Details:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Username:</span>
                    <span className="font-mono text-green-900">{username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Email:</span>
                    <span className="font-mono text-green-900">{email}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">Password Reset Instructions:</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-blue-700">1. Copy this reset URL:</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Input 
                        value={`${window.location.origin}/reset-password?token=${resetToken}`}
                        readOnly
                        className="text-xs font-mono"
                      />
                      <Button 
                        onClick={() => copyToClipboard(`${window.location.origin}/reset-password?token=${resetToken}`)}
                        size="sm"
                        variant="outline"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-blue-700">2. Or use this reset token:</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Input 
                        value={resetToken}
                        readOnly
                        className="text-xs font-mono"
                      />
                      <Button 
                        onClick={() => copyToClipboard(resetToken)}
                        size="sm"
                        variant="outline"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  asChild
                  className="w-full btn-primary"
                  size="lg"
                >
                  <Link to={`/reset-password?token=${resetToken}`}>
                    <Lock className="w-4 h-4 mr-2" />
                    Reset Password Now
                  </Link>
                </Button>

                <Button 
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="w-full bg-white/50"
                >
                  Search Another Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Card */}
        <Card className="glass-card animate-slide-in-right" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-4">
            <div className="text-center">
              <h3 className="font-semibold text-gray-800 text-sm mb-2">Available Test Accounts:</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• Username: <code>OmmNew</code> | Email: <code>ommnew@comica.test</code></div>
                <div>• Username: <code>demo</code> | Email: <code>demo@comica.test</code></div>
                <div>• Username: <code>manual_test</code> | Email: <code>manual@test.com</code></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordRecovery;